import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSnackbar } from 'contexts'
import {
  useCurrentUserCompany,
  useCertificateTriggerSettings,
  useUpdateCertificateActivationTrigger,
  useUpdateDataCollectionSettings,
  useUpdateWhiteLabelSetting,
  useAccessOverrideCounts,
  useCanWhiteLabel,
  useCanAddEmailDomain,
  type CertificateActivationTrigger,
} from 'hooks/api'
import {
  useCompanySettings,
  useAddEmailSendingDomain,
  useVerifyEmailSendingDomain,
  useRemoveEmailSendingDomain,
} from 'hooks/api/useEmailSendingDomain'
import { useUpdateCompanySettings } from 'hooks/api/useUpdateCompanySettings'
import { useReferrerState } from 'hooks'
import { PermissionGate } from 'components/access/PermissionGate'
import { getErrorMessage } from 'utils'
import { ROUTES } from 'routes'
import { EMAIL_DOMAIN_STATUSES } from 'constants/statuses'
// The activation triggers are defined ONCE, in constants/activationTriggers. This page used to
// carry a byte-identical copy of the list, the option type and the default — which is exactly the
// drift that file's docstring says it exists to prevent, and it is how the ON_PRIMARY_TRANSFER
// description came to name the wrong actor in one place and not the other. Aliased on import so
// the existing DEFAULT_TRIGGER call sites read unchanged.
import {
  ACTIVATION_TRIGGERS,
  DEFAULT_ACTIVATION_TRIGGER as DEFAULT_TRIGGER,
  type ActivationTriggerOption,
} from 'constants/activationTriggers'
// Same argument as the activation triggers above, one card down: the two
// certificate-access flags are defined ONCE in constants/accessSettings, and this
// page reads them instead of retyping them. It used to retype them, and the copies
// had already diverged — see the note on the toggle list below.
import {
  ACCESS_SETTINGS,
  accessSettingOption,
  type AccessSettingKey,
} from 'constants/accessSettings'
import { AccessOverrideRollup, ConfirmSettingModal, Header, InfoTooltip, StatusChip } from 'components'

import { useTheme, Typography, Stack, Box, Tooltip, alpha, IconButton, Divider } from '@mui/material'
import { CircleCheckBigIcon, LockIcon, XIcon, CopyIcon, CheckIcon, ArrowUpRightIcon } from 'lucide-react'

import { Card, Button, Skeleton, Switch, Input } from '@repo/ui'


export const FeaturesPage = () => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const navigate = useNavigate()
  // Carries "← Back to Features" to the plan page.
  const referrer = useReferrerState('Features')

  const companyQuery = useCurrentUserCompany()
  const companyId = companyQuery.data?.id ?? ''

  // White-label entitlement (Scale+, real — NOT Enterprise-only; see
  // WHITE_LABEL_TIER_KEYS) — the canonical "Hide Powered by Vera" toggle lives
  // here; the Studio card is a satellite that points back and reads/writes the
  // same persisted setting (TD-066).
  const { canWhiteLabel, minimumPlanTitle: whiteLabelMinPlan } = useCanWhiteLabel()

  const { settings, isLoading: settingsLoading } = useCertificateTriggerSettings(companyId, {
    enabled: !!companyId,
  })

  const updateMutation = useUpdateCertificateActivationTrigger()

  const isLoading = companyQuery.isLoading || (!!companyId && settingsLoading)

  // Persisted value (falls back to the default when not yet configured).
  const savedTrigger: CertificateActivationTrigger = settings?.activation_trigger ?? DEFAULT_TRIGGER

  const [selected, setSelected] = useState<CertificateActivationTrigger>(savedTrigger)

  // Sync local selection when the persisted value loads/changes.
  useEffect(() => {
    setSelected(savedTrigger)
  }, [savedTrigger])

  // Gate BEFORE applying, never after: if the tile visibly changed and the modal
  // then opened, cancelling would flip-and-revert and the UI would have lied
  // about what's stored for that moment. `pendingTrigger` holds the choice while
  // the user decides; `selected` only moves on confirm.
  const [pendingTrigger, setPendingTrigger] = useState<CertificateActivationTrigger | null>(null)

  const handleSelect = (option: ActivationTriggerOption) => {
    if (option.comingSoon) return
    if (option.id === selected) return
    setPendingTrigger(option.id)
  }

  // Confirmed, so persist. This setting decides when a certificate becomes
  // BILLABLE, which is why it's gated rather than saved silently on click like
  // the notification toggles.
  const handleConfirmTrigger = () => {
    if (!companyId || !pendingTrigger) return
    const next = pendingTrigger
    updateMutation.mutate(
      { companyId, activationTrigger: next },
      {
        onSuccess: () => {
          setSelected(next)
          setPendingTrigger(null)
        },
        onError: error => {
          setPendingTrigger(null)
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  // --- Data collection card (collect_email / collect_location) ---
  // Both default ON when not yet configured (VERA-316 AC).
  const savedCollectEmail = settings?.collect_email ?? true
  const savedCollectLocation = settings?.collect_location ?? true

  const dataCollectionMutation = useUpdateDataCollectionSettings()

  /*
    VRG-591 — how many products pin each flag instead of inheriting it, for the
    rollup under each toggle. See the hook for why certificates are not counted and
    what endpoint should replace the client-side derivation.
  */
  const {
    counts: overrideCounts,
    isLoading: isOverrideCountsLoading,
    certificatesCounted,
  } = useAccessOverrideCounts()

  const [collectEmail, setCollectEmail] = useState(savedCollectEmail)
  const [collectLocation, setCollectLocation] = useState(savedCollectLocation)

  // Sync local toggles when the persisted values load/change.
  useEffect(() => {
    setCollectEmail(savedCollectEmail)
    setCollectLocation(savedCollectLocation)
  }, [savedCollectEmail, savedCollectLocation])

  // Both gate before applying. Each governs every public certificate view —
  // whether a visitor must hand over an email, and whether we collect their
  // location — so they're consequential in the way a notification preference
  // isn't, even though all three are just switches.
  // Keyed by the COLUMN name, not by a local 'email' | 'location' shorthand, so the
  // pending entry can be handed straight to `accessSettingOption` and there is no
  // second vocabulary to translate between (the shorthand is what forced a mapping
  // helper at every call site once the copy moved into the constants file).
  const [pendingCollect, setPendingCollect] = useState<
    { key: AccessSettingKey; next: boolean } | null
  >(null)

  const handleConfirmCollect = () => {
    if (!companyId || !pendingCollect) return
    const { key, next } = pendingCollect
    const payload = {
      companyId,
      collect_email: key === 'collect_email' ? next : collectEmail,
      collect_location: key === 'collect_location' ? next : collectLocation,
    }
    dataCollectionMutation.mutate(payload, {
      onSuccess: () => {
        if (key === 'collect_email') setCollectEmail(next)
        else setCollectLocation(next)
        setPendingCollect(null)
      },
      onError: error => {
        setPendingCollect(null)
        notification({ type: 'error', title: getErrorMessage(error) })
      },
    })
  }

  // --- White label (hide_powered_by, TD-066) ---
  // Default OFF (badge shown) when not yet configured.
  const savedHidePoweredBy = settings?.hide_powered_by ?? false

  const whiteLabelMutation = useUpdateWhiteLabelSetting()

  const [hidePoweredBy, setHidePoweredBy] = useState(savedHidePoweredBy)

  // Sync local toggle when the persisted value loads/changes (e.g. updated
  // from the Studio white-label card).
  useEffect(() => {
    setHidePoweredBy(savedHidePoweredBy)
  }, [savedHidePoweredBy])

  const handleHidePoweredByChange = (checked: boolean) => {
    if (!companyId) return
    setHidePoweredBy(checked) // optimistic
    whiteLabelMutation.mutate(
      { companyId, hide_powered_by: checked },
      {
        // No success toast: the switch already flipped optimistically above.
        onError: error => {
          setHidePoweredBy(savedHidePoweredBy) // revert on failure
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  // --- Custom email sending domain (VERA-362) ---
  // Business plan or higher — a WIDER set than white-label (Scale+).
  const {
    canAddEmailDomain,
    minimumPlanTitle: emailDomainMinPlan,
    isLoading: emailEntitlementLoading,
  } = useCanAddEmailDomain()

  const companySettingsQuery = useCompanySettings(companyId)
  const emailSettings = companySettingsQuery.data

  const emailDomain = emailSettings?.email_sending_domain ?? null
  const emailDomainStatus = emailSettings?.email_domain_status ?? null
  const emailDomainDnsRecords = Object.entries(emailSettings?.email_domain_dns ?? {})

  const emailDomainLoading =
    isLoading || emailEntitlementLoading || companySettingsQuery.isLoading

  const [domainInput, setDomainInput] = useState('')
  const addDomainMutation = useAddEmailSendingDomain()
  const verifyDomainMutation = useVerifyEmailSendingDomain()
  const removeDomainMutation = useRemoveEmailSendingDomain()
  const [pendingRemoveDomain, setPendingRemoveDomain] = useState(false)

  const handleAddDomain = () => {
    const domain = domainInput.trim()
    if (!companyId || !domain) return
    addDomainMutation.mutate(
      { companyId, domain },
      {
        onSuccess: () => setDomainInput(''),
        onError: error => notification({ type: 'error', title: getErrorMessage(error) }),
      }
    )
  }

  const handleVerifyDomain = () => {
    if (!companyId) return
    verifyDomainMutation.mutate(
      { companyId },
      { onError: error => notification({ type: 'error', title: getErrorMessage(error) }) }
    )
  }

  const handleConfirmRemoveDomain = () => {
    if (!companyId) return
    removeDomainMutation.mutate(
      { companyId },
      {
        onSuccess: () => setPendingRemoveDomain(false),
        onError: error => {
          setPendingRemoveDomain(false)
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  // Custom link host — free text, so it gets a Save (§43), not apply-on-change.
  // Only usable once the sending domain is verified (a link host with no
  // verified domain has no CNAME to resolve).
  const savedLinkHost = emailSettings?.link_host ?? ''
  const [linkHostInput, setLinkHostInput] = useState(savedLinkHost)

  useEffect(() => {
    setLinkHostInput(savedLinkHost)
  }, [savedLinkHost])

  const linkHostDirty = linkHostInput.trim() !== savedLinkHost
  const updateCompanySettingsMutation = useUpdateCompanySettings()

  const handleSaveLinkHost = () => {
    if (!companyId) return
    updateCompanySettingsMutation.mutate(
      { companyId, settings: { link_host: linkHostInput.trim() || null } },
      { onError: error => notification({ type: 'error', title: getErrorMessage(error) }) }
    )
  }

  // Copy-to-clipboard for a DNS record's value — mirrors CreateDemoModal's copy pattern.
  const [copiedRecordKey, setCopiedRecordKey] = useState<string | null>(null)
  const handleCopyRecord = (key: string, value: string) => {
    navigator.clipboard.writeText(value)
    setCopiedRecordKey(key)
    window.setTimeout(() => setCopiedRecordKey(prev => (prev === key ? null : prev)), 1500)
  }

  // NO page-level Save. Every control here is a discrete choice, and the platform's
  // rule is that discrete choices apply on change (Notifications' toggles and the
  // Installation scan-destination picker both already work this way) while free-text
  // entry gets a Save. A deferred Save was also the wrong instrument for the risk:
  // it gated these changes without ever saying what they cost. The consequential
  // ones are gated by ConfirmSettingModal instead, which names the effect at the
  // moment of action. White-label was already on-change (TD-066) — it's now the
  // pattern rather than the exception.
  const pageSaving = updateMutation.isPending || dataCollectionMutation.isPending

  return (
    <Stack spacing={4}>
      {/* `nested` — see SupportPage: closes the 20px gap against the non-selfTitled sections. */}
      <Header title="Features" nested />
      <Card>
        <Stack spacing={4} pb={8}>
          <Stack direction="row" alignItems="center">
            <Typography variant="body1">Certificate activation triggers</Typography>
            {/* Definition comes from the glossary, not from a sentence typed here (§44). */}
            <InfoTooltip term="activation-trigger" />
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
            Choose when your certificates activate. This is one company-wide setting that
            applies to every certificate you issue. A certificate becomes billable when it
            activates, so this controls when each one starts counting toward your usage and
            charges. It also determines when scan and engagement data begins to be captured.
          </Typography>
        </Stack>

        {/* Horizontal tile row (like the profile theme selector) — the three
            triggers sit side-by-side so the section stays compact instead of a
            tall vertical list. Stretch keeps the tiles equal-height. */}
        <Stack direction="row" spacing={4} alignItems="stretch" role="radiogroup" aria-label="Certificate activation trigger" data-tour="features-activation-triggers">
          {isLoading
            ? // Loading skeleton — footprint-matches the option cards 1:1 (§19) so
              // skeleton → content causes zero layout shift. Same Card (p:6), same
              // background.default surface, same inner `Stack spacing={2}` with all
              // THREE regions of the real card: the title row (radio dot + body2 title),
              // the description (body3, one line), and the best_for row (pt:2 with
              // the 18px CircleCheckBigIcon + body3 text). Shared @repo/ui primitive.
              ACTIVATION_TRIGGERS.map((_, index) => (
                <Card
                  key={index}
                  sx={{
                    p: 6,
                    flex: 1,
                    height: '100%',
                    background: theme.palette.background.default,
                  }}
                >
                  <Stack spacing={2}>
                    {/* Title row — radio-dot placeholder, trigger-glyph placeholder,
                        then the title (18px line-height). The middle 18px block is
                        the icon's slot: without it the title would sit 30px left of
                        where it lands, so the skeleton → content swap would shift
                        the row. */}
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <Skeleton variant="circular" width={18} height={18} />
                      <Skeleton width={18} height={18} />
                      <Skeleton width={120} height={18} />
                    </Stack>
                    {/* Description — body3, single line (18px line-height). */}
                    <Skeleton width="80%" height={18} />
                    {/* best_for row — matches the pt:2 icon + body3 text row. */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 2 }}>
                      <Skeleton variant="circular" width={18} height={18} />
                      <Skeleton width="70%" height={18} />
                    </Stack>
                  </Stack>
                </Card>
              ))
            : ACTIVATION_TRIGGERS.map(option => {
                const isSelected = selected === option.id
                const disabled = !!option.comingSoon
                // Read from the option, never chosen here — the same three glyphs
                // have to appear on this card, the picker modal, the billing select
                // and the detail rows, and a trigger that changes symbol between
                // them stops being a recognisable thing.
                const Icon = option.icon
                return (
                  <Stack
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    role="radio"
                    aria-checked={isSelected}
                    aria-disabled={disabled}
                    sx={{ flex: 1, cursor: disabled ? 'default' : 'pointer' }}
                  >
                    <Card
                      sx={{
                        p: 6,
                        height: '100%',
                        opacity: disabled ? 0.6 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        borderColor: isSelected
                          ? theme.palette.brand[500]
                          : theme.palette.primary[100],
                        // Unselected cards sit on background.default (a step off the
                        // outer card's paper surface) so the nested cards read as
                        // distinct in light mode instead of paper-on-paper.
                        background: isSelected
                          ? alpha(theme.palette.brand[500], 0.1)
                          : theme.palette.background.default,
                        // Hover = lighter brand tint only (no brand border) so it reads
                        // as "considering"; the brand border is reserved for the
                        // selected ("chosen") state. A selected card keeps its full
                        // tint on hover rather than weakening.
                        '&:hover': disabled
                          ? undefined
                          : {
                              background: isSelected
                                ? alpha(theme.palette.brand[500], 0.1)
                                : alpha(theme.palette.brand[500], 0.06),
                            },
                      }}
                    >
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                          {/* Explicit selection indicator — parity with
                              ScanDestinationPicker (Installation "Where scans go"),
                              which follows this same §34 pattern plus this radio dot.
                              Border-only signal alone can be missed at a glance; this
                              makes "which one is chosen" unambiguous without a
                              screen-reader announcement it a second time (role="radio"
                              + aria-checked already carry that). */}
                          <Box
                            aria-hidden
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${
                                isSelected ? theme.palette.brand[500] : theme.palette.primary[100]
                              }`,
                              backgroundColor: isSelected ? theme.palette.brand[500] : 'transparent',
                              color: '#fff',
                            }}
                          >
                            {isSelected && <CheckIcon size={12} strokeWidth={2.5} />}
                          </Box>
                          {/* Between the selection dot and the title. 18px to match
                              the dot to its left and the "Best for" check below;
                              untinted, because selection is already carried by the
                              dot, the border and the card fill. */}
                          <Icon
                            aria-hidden
                            size={18}
                            strokeWidth={1}
                            color={theme.palette.text.secondary}
                            style={{ flexShrink: 0 }}
                          />
                          <Typography variant="body2">{option.title}</Typography>
                          {option.comingSoon && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: 4,
                                color: theme.palette.primary[600],
                                background: theme.palette.primary[100],
                              }}
                            >
                              Coming soon
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="body3" color="text.secondary">
                          {option.description}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 2 }}>
                          <CircleCheckBigIcon
                            strokeWidth={1}
                            size={18}
                            color={theme.palette.primary[600]}
                          />
                          <Typography
                            variant="body3"
                            sx={{ color: theme.palette.primary[600] }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              Best for
                            </Box>{' '}
                            {option.best_for.replace(/^Best for\s*/i, '')}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Card>
                  </Stack>
                )
              })}
        </Stack>
      </Card>

      <Card>
        <Stack spacing={4} pb={8}>
          <Stack direction="row" alignItems="center">
            <Typography variant="body1">Certificate access</Typography>
            {/*
              NO TOOLTIP HERE. It carried `term="scan"`, which defines what a scan IS — a
              different subject from what this section controls, which is what a visitor must
              give up before a public certificate will open. The word "scan" appears in the
              description below ("this data powers scan analytics"), and that is almost
              certainly why the term was reached for; a word appearing NEAR a heading is not
              the same as the heading being about it.

              Removed rather than re-pointed: there is no glossary term for certificate access,
              and §44 is explicit that the fix is to define the term first, never to type a
              definition into a call site. The description directly beneath already explains
              the section in full, so a tooltip explaining something else is strictly worse
              than none — it is read at the moment of confusion and answers a question the
              reader did not ask.

              If this section should carry one, add a `certificate-access` entry to
              `docs/glossary.json` and reference it. That is a content decision, not a
              styling one.
            */}
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
            Choose what to collect when someone opens one of your public certificates. This
            data powers scan analytics and helps you build your audience.
          </Typography>
        </Stack>

        {isLoading ? (
          <Stack spacing={4}>
            {[0, 1].map(index => (
              <Stack key={index} spacing={4}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Skeleton width="30%" height={20} />
                    <Skeleton lines={1} />
                    {/* The override-rollup line. §19 footprint parity — without it the
                        card grows by a line when the data lands and the toggles jump. */}
                    <Skeleton width={180} height={16} />
                  </Stack>
                  <Skeleton variant="rounded" width={42} height={22} />
                </Stack>
                {index !== 1 && <Divider />}
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={4}>
            {/* Plain rows, not bordered cells — these are two independent toggles (no
                "selected among a group" state for a border to carry, unlike the activation
                trigger tiles above or Installation's scan-destination picker, which are
                genuinely selectable-card groups per §34). A Divider separates them instead,
                matching NotificationsPage's toggle-list pattern.

                The label, the description and the consequence copy all come from
                ACCESS_SETTINGS now. They used to be typed out here, and had already
                drifted from it: this card still described location as something
                visitors are "asked for, with their consent" after the setting became a
                hard gate, and called it "Collect location" while the detail rows called
                it something else. That is the exact failure the constants file's own
                header says it exists to prevent — the fix is for this page to READ the
                single source, not for someone to remember to edit two places. */}
            {ACCESS_SETTINGS.map((option, index) => {
              const checked = option.key === 'collect_email' ? collectEmail : collectLocation
              const rollup = overrideCounts[option.key]

              return (
                <Stack key={option.key} spacing={4}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="body3" color="text.secondary">
                        {option.description}
                      </Typography>
                      {/* The company toggle is not the whole answer once products and
                          certificates can override it, and until this line existed
                          nothing on any surface said so. */}
                      <AccessOverrideRollup
                        products={rollup.products}
                        productsPartial={rollup.productsPartial}
                        certificatesCounted={certificatesCounted}
                        loading={isOverrideCountsLoading}
                      />
                    </Stack>
                    {/* Stages the change; the switch only moves once confirmed. */}
                    <Switch
                      checked={checked}
                      disabled={pageSaving}
                      onChange={(_e, next) => setPendingCollect({ key: option.key, next })}
                    />
                  </Stack>
                  {index !== ACCESS_SETTINGS.length - 1 && <Divider />}
                </Stack>
              )
            })}
          </Stack>
        )}
      </Card>

      {/* Email sending domain — VERA-362. Plan-gated Business+ (a wider
          set than white-label's Scale+). The "Sender identity" group label that
          used to sit above this + the SMS sender number card was removed with
          that card (VRG-487, 2026-08-30) — a heading grouping exactly one card
          has nothing left to distinguish it from. Per-brand SMS numbers are a
          separate, unbuilt feature; every company's SMS instead carries a
          VERA/company-name prefix per notification.sendgrid.service.ts's
          buildSmsSenderPrefix, governed by the White label toggle BELOW this
          card — reordered under it (15f3fc3c2, 2026-08-30) so Email sending
          domain reads as the more foundational setting; the toggle it depends
          on no longer sits above. */}
      <Card>
        <Stack spacing={4} pb={8}>
          {/* Dropped "Custom" from the visible title — it's the only email domain
              setting on the page, and the description line already says "your own
              domain", so "Custom" was qualifying a noun with no sibling to
              distinguish it from. */}
          <Typography variant="body1">Email sending domain</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
            Send your transactional emails — transfers, claims, receipts — from your own
            domain instead of no-reply@vera.inc. Available on the Business plan and higher.
          </Typography>
        </Stack>

        {emailDomainLoading ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Skeleton width="30%" height={20} />
              <Skeleton lines={1} />
            </Stack>
            <Skeleton variant="rounded" width={100} height={36} />
          </Stack>
        ) : !canAddEmailDomain ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="body2">Add a sending domain</Typography>
              <Typography variant="body3" color="text.secondary">
                Available on the Business plan and higher.
              </Typography>
            </Stack>
            <Tooltip title={`Available on the ${emailDomainMinPlan ?? 'Business'} plan and higher.`}>
              <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Button
                  variant="secondary"
                  startIcon={<LockIcon size={14} />}
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() =>
                    navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })
                  }
                >
                  Upgrade to {emailDomainMinPlan ?? 'Business'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ) : !emailDomain ? (
          <Stack spacing={3}>
            <Input
              label="Domain"
              placeholder="mail.yourbrand.com"
              value={domainInput}
              onChange={setDomainInput}
            />
            <Stack direction="row" justifyContent="flex-end">
              <PermissionGate permission="settings_manage_brand_email">
                {/* `disabledReason` (not a bare `disabled`) so this renders the Button's
                    intentional muted-but-legible disabled ink (~3.4:1, VRG-407) instead of
                    MUI's own near-invisible native `.Mui-disabled` override, which wins over
                    any sx color on a plainly `disabled` button regardless of what this file
                    says. See Button.tsx's `disabledReason` doc comment. */}
                <Button
                  variant="primary"
                  disabled={!domainInput.trim()}
                  disabledReason={!domainInput.trim() ? 'Enter a domain first' : undefined}
                  loading={addDomainMutation.isPending}
                  onClick={handleAddDomain}
                >
                  Add domain
                </Button>
              </PermissionGate>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={4}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">{emailDomain}</Typography>
                  {emailDomainStatus && (
                    <StatusChip
                      statuses={EMAIL_DOMAIN_STATUSES}
                      status={emailDomainStatus}
                      size="small"
                    />
                  )}
                </Stack>
                <Typography variant="body3" color="text.secondary">
                  {emailDomainStatus === 'verified'
                    ? 'Your transactional emails now send from this domain.'
                    : emailDomainStatus === 'failed'
                      ? 'Verification failed. If you just added the records, DNS changes can take a few minutes to propagate. Wait a bit and try again, or check the records below.'
                      : 'Add the DNS records below, then verify.'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={3} alignItems="center">
                {emailDomainStatus !== 'verified' && (
                  <PermissionGate permission="settings_manage_brand_email">
                    <Button
                      variant="secondary"
                      loading={verifyDomainMutation.isPending}
                      onClick={handleVerifyDomain}
                    >
                      Verify
                    </Button>
                  </PermissionGate>
                )}
                {/* A labelled "Remove" button, not a bare icon — it sits directly beside the
                    text button "Verify" and needs equal visual weight to read as its peer
                    rather than an afterthought. Still an X glyph (not a trash can): this
                    detaches the company's own setting rather than destroying a customer
                    record (§38) — the icon says WHAT KIND of action, the label says WHICH
                    one, together they're unambiguous with no confirmation-modal reading. */}
                <PermissionGate permission="settings_manage_brand_email">
                  <Button
                    variant="secondary"
                    startIcon={<XIcon strokeWidth={1} size={16} />}
                    onClick={() => setPendingRemoveDomain(true)}
                  >
                    Remove
                  </Button>
                </PermissionGate>
              </Stack>
            </Stack>

            {/* Divider, not a bordered box — separates two distinct stacked panels (the
                domain status row above from the DNS/link-host panel below) the same way
                Certificate access's toggle rows are separated, rather than nesting a card
                inside a card. */}
            {emailDomainStatus !== 'verified' && emailDomainDnsRecords.length > 0 && (
              <>
                <Divider />
                <Stack spacing={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Add these DNS records</Typography>
                      {/* Plain text + arrow, not a nested button (matches the
                          "Learn more in Features" pattern elsewhere on this page's
                          sibling components) — links to the durable setup guide
                          rather than trying to explain DNS records inline. */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(ROUTES.DOC('company-settings', 'settings-features'))}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          e.preventDefault()
                          navigate(ROUTES.DOC('company-settings', 'settings-features'))
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: theme.palette.text.secondary,
                          '&:hover': { color: theme.palette.text.primary },
                        }}
                      >
                        <Typography variant="body3" sx={{ color: 'inherit' }}>
                          View setup guide
                        </Typography>
                        <ArrowUpRightIcon size={14} strokeWidth={1.5} />
                      </Stack>
                    </Stack>
                    <Typography variant="body3" color="text.secondary">
                      Add these at your DNS provider. Changes can take a few minutes up to
                      48 hours to propagate depending on your provider, so a Verify attempt
                      right after adding them may not succeed yet.
                    </Typography>
                  </Stack>
                  {emailDomainDnsRecords.map(([key, record]) => (
                    <Stack key={key} spacing={2}>
                      <Typography variant="body3" color="text.secondary">
                        {record.type?.toUpperCase()} record
                      </Typography>
                      {/* Two fields, not one — a DNS record has a NAME (what to call the
                          new record) and a VALUE (what it points to), and most people
                          adding one for the first time don't know their registrar's form
                          wants BOTH, not just the destination. The old single-field
                          layout only showed the value; the name was plain text above it,
                          which read as a caption rather than something to copy-paste. */}
                      <Stack direction="row" spacing={3}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Input
                            label="Host"
                            readOnly
                            value={record.host}
                            endAdornment={
                              <IconButton
                                aria-label={`Copy ${record.type} record host`}
                                onClick={() => handleCopyRecord(`${key}-host`, record.host)}
                                size="small"
                              >
                                {copiedRecordKey === `${key}-host` ? (
                                  <CheckIcon strokeWidth={1} size={18} />
                                ) : (
                                  <CopyIcon strokeWidth={1} size={18} />
                                )}
                              </IconButton>
                            }
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Input
                            label="Value"
                            readOnly
                            value={record.data}
                            endAdornment={
                              <IconButton
                                aria-label={`Copy ${record.type} record value`}
                                onClick={() => handleCopyRecord(`${key}-value`, record.data)}
                                size="small"
                              >
                                {copiedRecordKey === `${key}-value` ? (
                                  <CheckIcon strokeWidth={1} size={18} />
                                ) : (
                                  <CopyIcon strokeWidth={1} size={18} />
                                )}
                              </IconButton>
                            }
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {emailDomainStatus === 'verified' && (
              <>
                <Divider />
                <Stack spacing={3}>
                  <Input
                    label="Custom link host (optional)"
                    placeholder="transfers.yourbrand.com"
                    value={linkHostInput}
                    onChange={setLinkHostInput}
                  />
                  <Typography variant="body3" color="text.secondary">
                    Links inside your transactional emails, like claim links, use this host
                    instead of Vera&apos;s. Leave blank to use the default.
                  </Typography>
                  <Stack direction="row" justifyContent="flex-end">
                    <PermissionGate permission="settings_manage_brand_email">
                      <Button
                        variant="primary"
                        disabled={!linkHostDirty}
                        disabledReason={!linkHostDirty ? 'Change the link host to save' : undefined}
                        loading={updateCompanySettingsMutation.isPending}
                        onClick={handleSaveLinkHost}
                      >
                        Save
                      </Button>
                    </PermissionGate>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Card>

      {/* White label — canonical "Hide Powered by Vera" toggle. Gated to
          Enterprise (real entitlement); the Studio white-label card is a satellite
          that points here and reads/writes the same persisted setting (TD-066). */}
      <Card>
        <Stack spacing={4} pb={8}>
          <Typography variant="body1" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            White label
            {/*
              The definition matters here specifically because the sentence below is easy to
              over-read: "remove Vera branding" sounds like it governs whether your OWN
              branding appears, and it does not. Putting your logo and colours on a
              certificate is ungated on every plan. This setting removes Vera's attribution,
              nothing else — which is exactly the distinction the glossary entry draws.
            */}
            <InfoTooltip term="white-label" />
          </Typography>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
            Remove Vera branding from your customer-facing certificate pages, email
            footers, and SMS notifications. Available on the {whiteLabelMinPlan ?? 'Scale'}{' '}
            plan and higher.
          </Typography>
        </Stack>

        {/* Plain row, not a bordered cell — a single toggle with no sibling rows in this
            Card has nothing for a border to separate it from (§ the Certificate access fix
            above applies the same reasoning). */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="body2">Hide &ldquo;Powered by Vera&rdquo;</Typography>
            <Typography variant="body3" color="text.secondary">
              Remove the &ldquo;Powered by Vera&rdquo; attribution from your public
              certificate pages, email footers, and SMS notifications — SMS drops the
              &ldquo;(via vera.inc)&rdquo; qualifier and leads with just your name instead.
            </Typography>
          </Stack>

          {canWhiteLabel ? (
            <Switch
              checked={hidePoweredBy}
              disabled={whiteLabelMutation.isPending}
              onChange={(_e, checked) => handleHidePoweredByChange(checked)}
            />
          ) : (
            <Tooltip title={`Available on the ${whiteLabelMinPlan ?? 'Scale'} plan and higher.`}>
              <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Button
                  variant="secondary"
                  startIcon={<LockIcon size={14} />}
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() =>
                    navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })
                  }
                >
                  {/* Names the SPECIFIC plan, not a generic "Upgrade to unlock" —
                      different features unlock at different tiers (this one's
                      floor is Scale, the email domain card below is Business),
                      so a generic CTA doesn't tell the customer which plan
                      actually solves their problem. */}
                  Upgrade to {whiteLabelMinPlan ?? 'Scale'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Card>

      {/* Consequence gates. Each names what the change actually does — the reason
          these are modals and not a Save button. */}
      <ConfirmSettingModal
        open={pendingTrigger !== null}
        onClose={() => setPendingTrigger(null)}
        onConfirm={handleConfirmTrigger}
        title="Change when certificates activate?"
        consequence="A certificate becomes billable when it activates, so this changes when charges start for every certificate created from now on. It does not affect certificates that have already activated."
        confirmLabel="Change activation trigger"
        loading={updateMutation.isPending}
      />

      <ConfirmSettingModal
        open={pendingCollect !== null}
        onClose={() => setPendingCollect(null)}
        onConfirm={handleConfirmCollect}
        /*
          Title, consequence and confirm verb all read from ACCESS_SETTINGS at
          `company` scope. The four sentences that used to be inlined here were the
          drifted copies: the location pair still promised a prompt "with their
          consent" and framed the OFF direction as merely losing the map, when the
          ON direction now bars a visitor who declines from seeing the details at
          all. The confirm button also read a bare "Turn on" / "Turn off", which
          §43 rules out — the label has to name the action.
        */
        title={
          pendingCollect
            ? `${
                pendingCollect.next
                  ? accessSettingOption(pendingCollect.key).confirmOn
                  : accessSettingOption(pendingCollect.key).confirmOff
              } on every certificate?`
            : ''
        }
        consequence={
          pendingCollect
            ? pendingCollect.next
              ? accessSettingOption(pendingCollect.key).consequenceOn('company')
              : accessSettingOption(pendingCollect.key).consequenceOff('company')
            : ''
        }
        confirmLabel={
          pendingCollect
            ? pendingCollect.next
              ? accessSettingOption(pendingCollect.key).confirmOn
              : accessSettingOption(pendingCollect.key).confirmOff
            : ''
        }
        loading={dataCollectionMutation.isPending}
      />

      <ConfirmSettingModal
        open={pendingRemoveDomain}
        onClose={() => setPendingRemoveDomain(false)}
        onConfirm={handleConfirmRemoveDomain}
        title="Remove custom email sending domain?"
        consequence="Your transactional emails will go back to sending from no-reply@vera.inc. Any custom link host you've set will stop taking effect (it only applies while a domain is verified)."
        confirmLabel="Remove domain"
        loading={removeDomainMutation.isPending}
      />
    </Stack>
  )
}
